/**
 * Constantes & helpers du module Inventaire.
 *
 * - STATUSES         : ordre canonique des statuts (filtre, dropdown)
 * - STATUS_COLORS    : palette badge par statut
 * - isLowStock/...   : helpers de calcul d'état d'un produit
 * - itemValue/...    : helpers monétaires
 * - quantityToOrder  : qté suggérée pour reach max_stock
 * - KEG_PCT_OPTIONS  : valeurs autorisées du dropdown % restant
 */

import type { InventoryStatus } from './types';

// ─────────────────────────────────────────────────────────────
// Statuts — ordre d'affichage canonique
// ─────────────────────────────────────────────────────────────
export const STATUSES: InventoryStatus[] = [
  'Actif',
  'En Commande',
  'En attente',
  'B/O',
  'Épuisement des stocks',
  'Édition limité',
  'Inactif',
];

// ─────────────────────────────────────────────────────────────
// Couleurs badge par statut (palette v123)
// ─────────────────────────────────────────────────────────────
export interface StatusColor {
  bg: string;
  text: string;
  border: string;
}

export const STATUS_COLORS: Record<InventoryStatus, StatusColor> = {
  'Actif':                 { bg: 'rgba(107,203,119,0.12)', text: '#6BCB77', border: 'rgba(107,203,119,0.40)' },
  'Inactif':               { bg: 'rgba(136,136,136,0.12)', text: '#888888', border: 'rgba(136,136,136,0.40)' },
  'En Commande':           { bg: 'rgba(136,187,255,0.12)', text: '#88BBFF', border: 'rgba(136,187,255,0.40)' },
  'En attente':            { bg: 'rgba(212,165,116,0.12)', text: '#D4A574', border: 'rgba(212,165,116,0.40)' },
  'B/O':                   { bg: 'rgba(255,148,77,0.12)',  text: '#FF944D', border: 'rgba(255,148,77,0.40)'  },
  'Épuisement des stocks': { bg: 'rgba(255,209,102,0.12)', text: '#FFD166', border: 'rgba(255,209,102,0.40)' },
  'Édition limité':        { bg: 'rgba(181,128,255,0.12)', text: '#B580FF', border: 'rgba(181,128,255,0.40)' },
};

// ─────────────────────────────────────────────────────────────
// Helpers d'état d'un produit
// ─────────────────────────────────────────────────────────────
type StockShape = {
  status: InventoryStatus;
  quantity: number;
  min_stock: number;
};

/**
 * Tolérance avant de marquer un item « bas stock ».
 * Règle v123 : il faut un écart d'au moins 0,49 sous le min pour
 * considérer l'item à commander. Évite les faux positifs quand qty
 * est juste à min (ex : qty=1, min=1 ne déclenche pas une commande).
 */
export const LOW_STOCK_BUFFER = 0.49;

/** Bas stock : Actif, qty > 0 et qty ≤ min_stock − LOW_STOCK_BUFFER. */
export function isLowStock(item: StockShape): boolean {
  return (
    item.status === 'Actif' &&
    item.min_stock > 0 &&
    item.quantity > 0 &&
    item.quantity <= item.min_stock - LOW_STOCK_BUFFER
  );
}

/** Rupture : Actif, qty = 0 et min_stock > 0. */
export function isOutOfStock(item: StockShape): boolean {
  return (
    item.status === 'Actif' &&
    item.quantity === 0 &&
    item.min_stock > 0
  );
}

/** OK : Actif et qty > min_stock. */
export function isOk(item: StockShape): boolean {
  return item.status === 'Actif' && item.quantity > item.min_stock;
}

/**
 * Visible à la vente — source unique de vérité pour le public (/degustation),
 * le prix des cocktails et l'import Cluster.
 *
 * Règle métier : il faut du stock ET un statut autre qu'« Inactif ».
 * Seul « Inactif » cache toujours ; B/O, En attente, En Commande,
 * Épuisement, Édition limité restent visibles tant qu'il reste du stock.
 *
 * (min_stock n'entre pas dans la règle — pas requis ici.)
 */
export function isAvailableForSale(item: {
  status: InventoryStatus;
  quantity: number;
}): boolean {
  return item.quantity > 0 && item.status !== 'Inactif';
}

/**
 * Indicateur résumé pour styling : 'ok' | 'low' | 'out' | 'inactive'.
 * Utilisé pour la couleur de la cellule "Qté" dans le tableau.
 */
export type StockState = 'ok' | 'low' | 'out' | 'inactive';

export function stockState(item: StockShape): StockState {
  if (item.status !== 'Actif') return 'inactive';
  if (isOutOfStock(item)) return 'out';
  if (isLowStock(item)) return 'low';
  return 'ok';
}

export const STOCK_STATE_COLORS: Record<StockState, string> = {
  ok:       '#6BCB77', // vert
  low:      '#FFD166', // orange / jaune
  out:      '#FF6B6B', // rouge
  inactive: '#888888', // gris
};

// ─────────────────────────────────────────────────────────────
// Helpers monétaires
// ─────────────────────────────────────────────────────────────

/** Valeur d'un item = price * quantity (0 si null). */
export function itemValue(item: { price: number | null; quantity: number }): number {
  return (item.price ?? 0) * (item.quantity ?? 0);
}

/** Groupe de valeur affiché dans le récap : nourriture / alcool / autres. */
export type ValueGroup = 'food' | 'alcohol' | 'other';

const normLabel = (s: string) =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();

/** Catégories non alcoolisées (hors nourriture). Toute autre catégorie
 *  (spiritueux, vins, bières, cidre…) compte comme alcool. */
const OTHER_CATEGORY_LABELS = new Set(
  ['Canette', 'Fourniture', 'Gaz', 'Purée & Sirop', 'Sans alcool', 'Vrac'].map(normLabel)
);

export function valueGroupOf(categoryLabel: string | null | undefined): ValueGroup {
  if (!categoryLabel) return 'other';
  const l = normLabel(categoryLabel);
  if (l === 'nourriture') return 'food';
  if (OTHER_CATEGORY_LABELS.has(l)) return 'other';
  return 'alcohol';
}

/**
 * Quantité à commander pour atteindre max_stock.
 * Toujours >= 1 par défaut quand on est en bas stock (cf. v123).
 */
export function quantityToOrder(item: {
  quantity: number;
  max_stock: number;
}): number {
  return Math.max(1, Math.ceil((item.max_stock ?? 0) - (item.quantity ?? 0)));
}

/** Format CAD pour les montants ($X,XXX.XX $). */
export function formatMoney(amount: number | null | undefined): string {
  const value = amount ?? 0;
  return value.toLocaleString('fr-CA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + ' $';
}

/**
 * Format décimal québécois (virgule comme séparateur).
 *   formatDecimal(8.5)        → "8,5"
 *   formatDecimal(8)          → "8"
 *   formatDecimal(8.5, 2)     → "8,50"
 *   formatDecimal(null)       → ""
 */
export function formatDecimal(
  n: number | null | undefined,
  fractionDigits?: number
): string {
  if (n === null || n === undefined || !Number.isFinite(n)) return '';
  const opts: Intl.NumberFormatOptions =
    fractionDigits !== undefined
      ? { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits }
      : { maximumFractionDigits: 2 };
  return n.toLocaleString('fr-CA', opts);
}

/**
 * Parse une saisie utilisateur en nombre. Accepte virgule OU point décimal.
 *   parseDecimal("8,5")  → 8.5
 *   parseDecimal("8.5")  → 8.5
 *   parseDecimal("")     → null
 *   parseDecimal("abc")  → null
 */
export function parseDecimal(s: string): number | null {
  if (!s) return null;
  const cleaned = s.trim().replace(/\s/g, '').replace(',', '.');
  if (cleaned === '') return null;
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : null;
}

// ─────────────────────────────────────────────────────────────
// Fûts (kegs)
// ─────────────────────────────────────────────────────────────

/** Pourcentages autorisés sur le dropdown de % restant (v123). */
export const KEG_PCT_OPTIONS = [0, 25, 50, 75, 100] as const;

export const KEG_LITERS_OPTIONS = [20, 30, 50] as const;

/** Couleur du badge % restant : vert >50 / jaune >20 / rouge sinon. */
export function kegPctColor(pct: number): string {
  if (pct > 50) return '#6BCB77';
  if (pct > 20) return '#FFD166';
  return '#FF6B6B';
}

// ─────────────────────────────────────────────────────────────
// Permissions IDs (typage doux pour éviter les fautes de frappe)
// ─────────────────────────────────────────────────────────────
export const PERM_INVENTORY_VIEW      = 'inventory.view'      as const;
export const PERM_INVENTORY_ORDERS    = 'inventory.orders'    as const;
export const PERM_INVENTORY_SUPPLIERS = 'inventory.suppliers' as const;
export const PERM_INVENTORY_ARCHIVES  = 'inventory.archives'  as const;
export const PERM_INVENTORY_KEGS      = 'inventory.kegs'      as const;
